import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

const QUIET_PERIOD = 900

export default function useActivityArrivalFocus(targetRef) {
    const location = useLocation()
    const navigate = useNavigate()
    const [highlighted, setHighlighted] = useState(false)

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        if (params.get('focus') !== 'activities') return undefined
        const target = targetRef.current
        if (!target) return undefined
        const page = target.closest('[data-case-detail-page]') || target.closest('main') || target.parentElement
        let timer
        let clearCue
        let stopped = false
        let lastChange = Date.now()
        let previousLayout = ''
        const changed = () => { lastChange = Date.now() }
        const removeFocusParameter = () => {
            params.delete('focus')
            const query = params.toString()
            navigate(`${location.pathname}${query ? `?${query}` : ''}${location.hash}`, { replace: true })
        }
        const stopForUser = (event) => {
            if (event.type === 'keydown' && !['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) return
            stopped = true
            clearTimeout(timer)
            clearTimeout(clearCue)
            setHighlighted(false)
            removeFocusParameter()
        }
        const mutations = new MutationObserver(changed)
        mutations.observe(page, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-busy', 'data-loading', 'data-case-detail-ready'] })
        const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(changed)
        resize?.observe(page)
        const settle = () => {
            if (stopped) return
            const rect = target.getBoundingClientRect()
            const layout = `${Math.round(rect.top)}:${page.scrollHeight}:${Math.round(page.getBoundingClientRect().height)}`
            if (layout !== previousLayout) { previousLayout = layout; changed() }
            const busy = page.getAttribute('data-case-detail-ready') === 'false'
                || Array.from(page.querySelectorAll('.skeleton, [aria-busy="true"], [data-loading="true"]')).some((el) => el.getBoundingClientRect().height > 0)
                || Array.from(page.querySelectorAll('img')).some((image) => !image.complete)
                || document.fonts?.status === 'loading'
            if (busy || !rect.height) changed()
            if (busy || !rect.height || Date.now() - lastChange < QUIET_PERIOD) {
                timer = setTimeout(settle, 100)
                return
            }
            stopped = true
            mutations.disconnect()
            resize?.disconnect()
            target.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' })
            setHighlighted(true)
            clearCue = setTimeout(() => { setHighlighted(false); removeFocusParameter() }, 2200)
        }
        for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) window.addEventListener(event, stopForUser, { passive: true, capture: true })
        timer = setTimeout(settle, 100)
        return () => {
            stopped = true
            clearTimeout(timer)
            clearTimeout(clearCue)
            mutations.disconnect()
            resize?.disconnect()
            for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) window.removeEventListener(event, stopForUser, true)
        }
    }, [location.pathname, location.search, location.hash, navigate, targetRef])

    return highlighted
}
